import { router } from "@inertiajs/react";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useState } from "react";

import FaqCardComponent from "@/components/Cards/FaqComponent";
import Dropdown from "@/components/Dropdown";
import Layout from "@/components/Layout";
import Table from "@/components/Table";
import Typography from "@/components/Typography";
import WarningTooltip from "@/components/WarningTooltip";
import { FiltersContainer, ListSection, PageContainer } from "@/constants/listIndexStyle";
import { completMonth } from "@/utils/dateFormater";
import { getYearOptions } from "@/utils/getYearOptions";
import { moneyFormatterWithoutCurrency } from "@/utils/moneyFormat";
import { formatNumberWithCommas } from "@/utils/numberFormater";

const yearsOptions: string[] = getYearOptions();

interface FacilityData {
  id: number;
  code: string;
  consumer_name: string;
  address: string;
  monthly_kwh: number;
  allocation_value: number;
  allocation_type: "percentage" | "order" | "none";
  total_price?: number;
}

interface PowerPlantAccountsPageProps {
  consortiums: {
    id: number;
    name: string;
    injected_kwh: number;
    facilities: FacilityData[];
  }[];
}

const PowerPlantAccountsPage = ({ consortiums }: PowerPlantAccountsPageProps) => {
  const columnHelper = createColumnHelper();
  const today = new Date();

  const [filters, setFilters] = useState({
    year: new URLSearchParams(window.location.search).get("year")
      ? String(new URLSearchParams(window.location.search).get("year"))
      : String(today.getFullYear()),
    month: new URLSearchParams(window.location.search).get("month")
      ? completMonth[Number(new URLSearchParams(window.location.search).get("month")) - 1]
      : completMonth[today.getMonth() - 1],
  });

  // Transformar os dados para o formato hierárquico com subRows
  const tableData = consortiums.map((consortium) => ({
    id: consortium.id,
    name: consortium.name,
    injected_kwh: consortium.injected_kwh,
    subRows: consortium.facilities,
  }));

  const columns = [
    columnHelper.accessor("name", {
      header: () => <div className="w-full pl-6 text-left">SPE/Unidade Consumidora</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // Linha PAI: SPE
          return (
            <div className="flex w-full items-center gap-2 pl-6 text-left">
              <span className="font-semibold text-primary500">{info.row.original.name}</span>
            </div>
          );
        }
        // Linha FILHA: Unidade Consumidora
        return (
          <div className="flex w-full flex-col pl-6 text-left">
            <span className="text-sm font-medium text-primary500">
              {info.row.original.consumer_name}
            </span>
            <span className="text-xs text-gray-500">
              {info.row.original.code} - {info.row.original.address}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor("allocation_value", {
      header: () => <div className="w-full text-center">Rateio</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // SPE colapsada: mostrar traço
          return <div className="w-full text-center text-gray-400">-</div>;
        }
        // Facility: mostrar rateio
        const value = info.getValue();
        const type = info.row.original.allocation_type;

        if (type === "percentage") {
          return <div className="w-full text-center text-primary300">{value}%</div>;
        } else if (type === "order") {
          return <div className="w-full text-center text-primary300">{value}º</div>;
        }
        return <div className="w-full text-center text-gray-400">-</div>;
      },
    }),
    columnHelper.accessor("monthly_kwh", {
      header: () => <div className="w-full text-center">Injetado</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // SPE: SOMA dos consumos dos filhos
          const total = info.row.original.subRows.reduce(
            (sum: number, row: any) => sum + (row.injected_kwh_by_consortium || 0),
            0,
          );
          const injectedKwh = info.row.original.injected_kwh;
          return (
            <div className="w-full text-center font-semibold text-primary500">
              {total > 0
                ? `${formatNumberWithCommas(injectedKwh)} (${formatNumberWithCommas(total)} kWh)`
                : "-"}
            </div>
          );
        }
        const injectedValueByConsortium = info.row.original.injected_kwh_by_consortium;
        return (
          <div className="w-full text-center text-primary300">
            {injectedValueByConsortium != null
              ? `${formatNumberWithCommas(injectedValueByConsortium)} kWh`
              : "-"}
          </div>
        );
      },
    }),
    columnHelper.accessor("total_price", {
      header: () => <div className="w-full text-center">Valor</div>,
      cell: (info: any) => {
        if (info.row.depth === 0) {
          // SPE: SOMA dos valores dos filhos
          const total = info.row.original.subRows.reduce(
            (sum: number, row: any) => sum + (row.total_price_by_consortium || 0),
            0,
          );
          return (
            <div className="w-full text-center font-semibold text-primary500">
              {total > 0 ? `R$ ${moneyFormatterWithoutCurrency(total)}` : "-"}
            </div>
          );
        }
        // Facility: mostrar valor individual
        const value = info.row.original.total_price_by_consortium;
        return (
          <div className="w-full text-center text-primary300">
            {value ? `R$ ${moneyFormatterWithoutCurrency(value)}` : "R$ 0,00"}
          </div>
        );
      },
    }),
  ];

  useEffect(() => {
    const monthIndex = completMonth.indexOf(filters.month) + 1;
    const validMonthIndex = monthIndex > 0 ? monthIndex : 1;

    // Atualiza a URL com os novos filtros
    const url = new URL(window.location.href);
    url.searchParams.set("year", filters.year);
    url.searchParams.set("month", validMonthIndex.toString());
    window.history.replaceState({}, "", url.toString());

    // Recarrega a página com os novos filtros
    router.reload({
      data: {
        year: filters.year,
        month: validMonthIndex,
      },
    });
  }, [filters]);

  const exportTable = () => {
    const data: string[][] = [
      ["SPE", "Unidade Consumidora", "Código", "Endereço", "Rateio", "Consumo", "Valor"],
    ];

    consortiums.forEach((consortium) => {
      consortium.facilities.forEach((facility: FacilityData) => {
        data.push([
          consortium.name,
          facility.consumer_name,
          facility.code,
          facility.address,
          facility.allocation_type === "percentage"
            ? `${facility.allocation_value}%`
            : facility.allocation_type === "order"
              ? `${facility.allocation_value}º`
              : "-",
          facility.monthly_kwh != null ? formatNumberWithCommas(facility.monthly_kwh) : "0",
          facility.total_price != null ? formatNumberWithCommas(facility.total_price) : "0",
        ]);
      });
    });

    const csv = "data:text/csv;charset=utf-8," + data.map((e) => e.join(";")).join("\n");
    const encodedUri = encodeURI(csv);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "geedee-faturamento-por-usina.csv");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  };

  return (
    <Layout>
      <PageContainer>
        <Typography variant="h3">Faturamento por Usina</Typography>

        <ListSection className="flex-col md:flex-row">
          <Typography variant="h4">Listagem</Typography>

          <FiltersContainer className="flex items-center gap-4 pt-5 md:pt-0">
            <WarningTooltip message="Atenção: Para garantir resultados corretos, é essencial que todas as configurações estejam devidamente ajustadas. Certifique-se de que a carta de rateio, as contas de energia e os valores de energia injetada pela usina estejam exatos. Além disso, as configurações da FATTUREWEB de cada unidade consumidora devem estar completamente corretas e atualizadas. Qualquer inconsistência pode impactar diretamente nos cálculos e resultados apresentados." />
            <Dropdown
              options={yearsOptions}
              onSelect={(value) => {
                setFilters({ ...filters, year: value });
              }}
              defaultValue={filters.year}
            />
            <Dropdown
              options={completMonth}
              onSelect={(value) => {
                setFilters({ ...filters, month: value });
              }}
              defaultValue={filters.month}
            />
          </FiltersContainer>
        </ListSection>

        <Table data={tableData} columns={columns} onClickRow={null} />

        <div className="mt-6 flex w-[500px] flex-row gap-4">
          <button
            className="flex h-[40px] w-full cursor-pointer flex-row items-center justify-center rounded-full border-[none] bg-slate-950 p-4 text-white transition-colors hover:bg-slate-800"
            onClick={exportTable}
          >
            Exportar tabela
          </button>
        </div>

        <div className="pt-18">
          <FaqCardComponent variant="questionSection" />
        </div>
      </PageContainer>
    </Layout>
  );
};

export default PowerPlantAccountsPage;
